/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { PeopleApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/** One signature in a PDF, as the jobs service read and checked it — see Signing/PdfSignatureVerifier.cs. */
export interface PdfSignature {
  field: string;
  name?: string | null;
  reason?: string | null;
  location?: string | null;
  signedAt?: string | null;
  signer?: string | null;
  issuer?: string | null;
  thumbprint?: string | null;
  /** The signature verifies over the bytes it covers. */
  intact: boolean;
  /** Nothing was added to the file after this signature. */
  coversWholeFile: boolean;
  /** Signed with the organisation's certificate, or one a trusted authority issued. */
  trusted: boolean;
  problem?: string | null;
}

/** valid: intact and nothing after it · later: intact, but the file went on (another signature or an edit) · broken · unknown: intact but not the organisation's. */
export type SignatureState = 'valid' | 'later' | 'broken' | 'unknown';

export interface SignOptions {
  reason?: string;
  location?: string;
  majorVersion?: boolean;
}

export interface SignResult {
  versionLabel?: string | null;
  signatures: PdfSignature[];
}

export interface SigningCertificateInfo {
  subject: string;
  issuer: string;
  thumbprint: string;
  notBefore: string;
  notAfter: string;
  selfSigned: boolean;
}

/**
 * Digital signatures. The organisation's key lives in the jobs service and
 * never reaches the browser: it signs the stored version of a document on the
 * caller's behalf, with their own credentials, and saves the result as a new
 * version under their name.
 */
@Injectable({ providedIn: 'root' })
export class PdfSigningService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthenticationService);
  private readonly api = inject(AlfrescoApiService);

  private get baseUrl(): string {
    return `${this.appConfig.get<string>('jobs.baseUrl', '/jobs-api')}/api/sign`;
  }

  /** The signatures in the stored version; null when they could not be read (service down). */
  signatures(nodeId: string): Observable<PdfSignature[] | null> {
    return this.http
      .get<PdfSignature[]>(`${this.baseUrl}/nodes/${encodeURIComponent(nodeId)}/signatures`, { headers: this.credentials() })
      .pipe(catchError(() => of(null)));
  }

  sign(nodeId: string, options: SignOptions): Observable<SignResult> {
    return this.http.post<SignResult>(`${this.baseUrl}/nodes/${encodeURIComponent(nodeId)}`, options, { headers: this.credentials() });
  }

  certificate(): Observable<SigningCertificateInfo> {
    return this.http.get<SigningCertificateInfo>(`${this.baseUrl}/certificate`, { headers: this.credentials() });
  }

  /** Replaces the organisation's certificate with a PKCS#12 file (administrators only). */
  replaceCertificate(pfx: ArrayBuffer, password: string): Observable<SigningCertificateInfo> {
    return this.http.post<SigningCertificateInfo>(`${this.baseUrl}/certificate`, { pfx: toBase64(pfx), password }, { headers: this.credentials() });
  }

  /** The public certificate, for a reader to install as trusted. */
  get certificateUrl(): string {
    return `${this.baseUrl}/certificate.cer`;
  }

  /** The signed-in person's name as a signature will show it. */
  me(): Observable<string> {
    return from(new PeopleApi(this.api.getInstance() as any).getPerson('-me-')).pipe(
      map(({ entry }) => entry.displayName || [entry.firstName, entry.lastName].filter(Boolean).join(' ') || entry.id),
      catchError(() => of(this.auth.getUsername() ?? ''))
    );
  }

  static state(signature: PdfSignature): SignatureState {
    if (!signature.intact) {
      return 'broken';
    }
    if (!signature.trusted) {
      return 'unknown';
    }
    return signature.coversWholeFile ? 'valid' : 'later';
  }

  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
